// HEAL-82 — навигация вокруг загрузок: куда возвращать человека.
//
// ПРОБЛЕМА, которую это закрывает. Каждый экран решал сам и по-своему: кто
// шагал по истории браузера вслепую (`window.history.back()`), кто смотрел на
// один признак. Отсюда три дефекта сразу:
//
//   1. После удаления «Назад» не работал вовсе. Переход шёл на `/uploads` —
//      а это не экран, а перенаправление на `/upload/history`. И переход, и
//      перенаправление ДОБАВЛЯЛИ записи в историю:
//
//        /upload/history → /uploads/123 → /uploads → /upload/history
//
//      Человек на последней записи, жмёт «Назад», попадает на `/uploads`, и тот
//      мгновенно перекидывает его вперёд. Экран не меняется — кнопка выглядит
//      мёртвой, и так на каждое нажатие.
//
//   2. Даже пробившись через петлю, человек попадал на `/uploads/123` — то есть
//      на уже УДАЛЁННЫЙ анализ, где его встречало «не удалось загрузить».
//
//   3. «Вернусь позже» с экрана проверки вело по-разному: файл из пакета — в
//      пакет, одиночный — на Главную, независимо от того, откуда человек пришёл.
//      Разбирал старые анализы из «Моих загрузок» — и вылетал из раздела.
//
// РЕШЕНИЕ. У каждого экрана есть явный логический родитель, и возврат всегда
// идёт туда — независимо от того, каким путём человек сюда попал: из Профиля,
// по ссылке из письма, после удаления. Предсказуемо и не ломается.
//
// Где родителей несколько (экран проверки), «откуда пришли» запоминается
// состоянием перехода — тем же приёмом, что в карточках маркеров, — а не
// угадывается по истории браузера. Именно догадки и давали дефекты.

/** Разделы, из которых можно провалиться на экран проверки анализа. */
export type ReviewOrigin = 'home' | 'history' | { batchId: number };

let reviewOrigin: ReviewOrigin | null = null;

/**
 * Запомнить, откуда человек уходит на проверку анализа.
 *
 * Вызывается в момент перехода. Живёт до следующего перехода или до
 * перезагрузки страницы — тогда сработает безопасный запасной вариант.
 */
export function setReviewOrigin(origin: ReviewOrigin): void {
  reviewOrigin = origin;
}

/**
 * Куда вернуть человека с экрана проверки.
 *
 * Порядок предпочтения:
 *   1. запомненный раздел, откуда пришёл;
 *   2. пакет файла — запасной вариант, когда «откуда» неизвестно;
 *   3. Главная — нейтральная точка: там строка с напоминанием, задача не теряется.
 *
 * ⚠️ Порядок изменён 24.09 (находка ревью). Раньше пакет побеждал всегда, и это
 * противоречило вызывающему коду: `UploadDetail` явно объявляет
 * `setReviewOrigin('history')` с комментарием «вернёт его сюда же» — а функция
 * это объявление игнорировала, если у файла был пакет. Сценарий «Мои загрузки →
 * анализ → Проверить → Вернусь позже» выбрасывал человека из раздела.
 *
 * Прежний довод — «разбирать всё равно будет пакетом» — остаётся в силе там,
 * где он верен: из самого пакета раздел объявляется пакетом, и возврат ведёт
 * в него.
 */
export function reviewParent(batchId: number | null): string {
  if (reviewOrigin === 'history') return UPLOADS_SECTION;
  if (reviewOrigin === 'home') return HOME;

  if (batchId !== null) return `/uploads/batch/${batchId}`;
  if (typeof reviewOrigin === 'object' && reviewOrigin !== null) {
    return `/uploads/batch/${reviewOrigin.batchId}`;
  }

  return HOME;
}

export function clearReviewOrigin(): void {
  reviewOrigin = null;
}

/** Разделы, из которых попадают в «Мои загрузки». */
export type UploadsOrigin = 'home' | 'profile';

let uploadsOrigin: UploadsOrigin | null = null;

/**
 * Запомнить, откуда человек уходит в «Мои загрузки».
 *
 * Тот же разбор, что и у экрана проверки, и по той же причине: путей сюда
 * несколько — из Профиля, со строки на Главной, после подтверждения анализа.
 * Задавать родителя одним значением значит в половине случаев уводить человека
 * не туда, откуда он пришёл.
 */
export function setUploadsOrigin(origin: UploadsOrigin): void {
  uploadsOrigin = origin;
}

/**
 * Куда вернуть из «Моих загрузок».
 *
 * Профиль, если человек пришёл оттуда; иначе Главная. Признак живёт до
 * перезагрузки страницы — тогда сработает запасной вариант, и это правильно:
 * при прямом заходе по ссылке никакого «откуда» и не было.
 */
export function uploadsParent(): string {
  return uploadsOrigin === 'profile' ? PROFILE : HOME;
}

export function clearUploadsOrigin(): void {
  uploadsOrigin = null;
}

/**
 * Раздел «Мои загрузки» — КОНЕЧНЫЙ адрес, не перенаправляющий.
 *
 * `/uploads` тоже ведёт сюда, но через перенаправление, и каждый такой проход
 * плодит лишнюю запись в истории. Перенаправления нужны для старых внешних
 * ссылок; внутри приложения ходить через них незачем.
 */
export const UPLOADS_SECTION = '/upload/history';

export const HOME = '/';

export const PROFILE = '/profile';

/** Разделы, из которых открывают карточку одного анализа. */
export type UploadOrigin = 'history' | { batchId: number };

let uploadOrigin: UploadOrigin | null = null;

/** Запомнить, откуда человек уходит в карточку анализа. */
export function setUploadOrigin(origin: UploadOrigin): void {
  uploadOrigin = origin;
}

export function clearUploadOrigin(): void {
  uploadOrigin = null;
}

/**
 * Родитель экрана одного анализа.
 *
 * ⚠️ Правило изменено 24.09 по итогам проверки на устройстве. Раньше файл из
 * пакета ВСЕГДА возвращал в пакет, откуда бы человек ни пришёл. Замысел был
 * разумный — у завершённого пакета другого входа в интерфейсе нет, — но на
 * живом сценарии он обернулся дефектом: человек заходил в «Мои загрузки»,
 * открывал анализ месячной давности, жал «Назад» и оказывался на экране пакета,
 * из раздела он выпадал. И так каждый раз.
 *
 * Теперь запомненный раздел важнее пакета: возврат ведёт туда, откуда пришли.
 * Пакет остаётся запасным вариантом — для прямого захода по ссылке и после
 * перезагрузки, где «откуда» неизвестно, а вернуть человека куда-то надо.
 */
export function uploadParent(batchId: number | null): string {
  if (uploadOrigin === 'history') return UPLOADS_SECTION;

  if (typeof uploadOrigin === 'object' && uploadOrigin !== null) {
    return `/uploads/batch/${uploadOrigin.batchId}`;
  }

  return batchId !== null ? `/uploads/batch/${batchId}` : UPLOADS_SECTION;
}

// Родитель «Моих загрузок» больше не константа: он зависит от того, откуда
// человек пришёл — см. uploadsParent().
